/**
 * Modern Propane — client-side input guards & HTML escaping for forms/search.
 */
(function (global) {
  'use strict';

  var CONTROL_RE = /[\u0000-\u001F\u007F-\u009F]/g;
  var TAG_RE = /[<>]/g;

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function stripControl(value) {
    return String(value == null ? '' : value).replace(CONTROL_RE, '');
  }

  function sanitizeText(value, maxLength) {
    var s = stripControl(value).trim();
    if (typeof maxLength === 'number' && maxLength > 0 && s.length > maxLength) {
      s = s.slice(0, maxLength);
    }
    return s;
  }

  function sanitizeZip(value) {
    return String(value || '').replace(/\D/g, '').slice(0, 5);
  }

  function isZip(value) {
    return /^\d{5}$/.test(sanitizeZip(value));
  }

  function sanitizeEmail(value, maxLength) {
    return sanitizeText(value, maxLength || 254)
      .toLowerCase()
      .replace(/[^\w.@+-]/g, '');
  }

  function sanitizePhone(value, maxLength) {
    return stripControl(value)
      .replace(/[^\d+().\-\sx]/gi, '')
      .slice(0, maxLength || 30)
      .trim();
  }

  function sanitizeState(value) {
    return stripControl(value)
      .replace(/[^A-Za-z]/g, '')
      .slice(0, 2)
      .toUpperCase();
  }

  function sanitizeSearchQuery(value, maxLength) {
    var limit = maxLength || 120;
    var s = stripControl(value).replace(TAG_RE, '').trim();
    if (s.length > limit) s = s.slice(0, limit);
    return s;
  }

  function sanitizeUrl(value) {
    var s = String(value || '').trim();
    if (!s) return '';
    if (/^https?:\/\//i.test(s) || /^tel:/i.test(s) || /^mailto:/i.test(s)) return s;
    if (s.charAt(0) === '/') return s;
    return '';
  }

  function bindInput(el, fn) {
    if (!el || el.__mpGuardInput) return;
    el.__mpGuardInput = true;
    el.addEventListener('input', fn);
    el.addEventListener('blur', fn);
  }

  function bindFormSubmit(form, fn) {
    if (!form || form.__mpGuardSubmit) return;
    form.__mpGuardSubmit = true;
    form.addEventListener('submit', fn, true);
  }

  var GUARDS = {
    zip: function (el) {
      el.setAttribute('inputmode', 'numeric');
      el.setAttribute('maxlength', '5');
      el.setAttribute('pattern', '[0-9]{5}');
      if (!el.getAttribute('autocomplete')) el.setAttribute('autocomplete', 'postal-code');
      bindInput(el, function () {
        el.value = sanitizeZip(el.value);
      });
    },
    search: function (el) {
      el.setAttribute('maxlength', '120');
      el.setAttribute('spellcheck', 'false');
      el.setAttribute('autocorrect', 'off');
      el.setAttribute('autocomplete', 'off');
      bindInput(el, function () {
        var next = sanitizeSearchQuery(el.value, 120);
        if (next !== el.value) el.value = next;
      });
      bindFormSubmit(el.closest('form'), function () {
        el.value = sanitizeSearchQuery(el.value, 120);
      });
    },
    email: function (el) {
      el.setAttribute('maxlength', '254');
      bindFormSubmit(el.closest('form'), function () {
        el.value = sanitizeEmail(el.value);
      });
    },
    phone: function (el) {
      el.setAttribute('maxlength', '30');
      bindInput(el, function () {
        el.value = sanitizePhone(el.value);
      });
    },
    name: function (el) {
      var max = parseInt(el.getAttribute('data-max-length') || '100', 10);
      el.setAttribute('maxlength', String(max));
      bindInput(el, function () {
        el.value = sanitizeText(el.value, max);
      });
    },
    message: function (el) {
      var max = parseInt(el.getAttribute('data-max-length') || '5000', 10);
      el.setAttribute('maxlength', String(max));
      bindInput(el, function () {
        el.value = stripControl(el.value).slice(0, max);
      });
    },
    text: function (el) {
      var max = parseInt(el.getAttribute('data-max-length') || '200', 10);
      el.setAttribute('maxlength', String(max));
      bindInput(el, function () {
        el.value = sanitizeText(el.value, max);
      });
    },
    address: function (el) {
      el.setAttribute('maxlength', '200');
      bindInput(el, function () {
        el.value = sanitizeText(el.value, 200);
      });
    },
    state: function (el) {
      el.setAttribute('maxlength', '2');
      bindInput(el, function () {
        el.value = sanitizeState(el.value);
      });
    }
  };

  function protectElement(el) {
    var guard = el.getAttribute('data-mp-guard');
    if (!guard || !GUARDS[guard]) return;
    GUARDS[guard](el);
  }

  function protectAll(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-mp-guard]').forEach(protectElement);
    scope.querySelectorAll('input[type="search"]:not([data-mp-guard])').forEach(function (el) {
      el.setAttribute('data-mp-guard', 'search');
      protectElement(el);
    });
  }

  function init() {
    protectAll(document);
    document.addEventListener('shopify:section:load', function (evt) {
      if (evt.target) protectAll(evt.target);
    });
  }

  global.MPInputSecurity = {
    escapeHtml: escapeHtml,
    sanitizeText: sanitizeText,
    sanitizeZip: sanitizeZip,
    isZip: isZip,
    sanitizeEmail: sanitizeEmail,
    sanitizePhone: sanitizePhone,
    sanitizeState: sanitizeState,
    sanitizeSearchQuery: sanitizeSearchQuery,
    sanitizeUrl: sanitizeUrl,
    protectAll: protectAll,
    protectElement: protectElement
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(typeof window !== 'undefined' ? window : this);
